/**
 * Client for the Euler Data API (V3) — https://v3.euler.finance/v3/docs
 *
 * Replaces the Goldsky `euler-v2-<chain>` subgraphs (which Euler is
 * deprecating, and which were observed running ~7 weeks behind chain head on
 * BSC, silently dropping freshly-opened sub-account positions).
 *
 * For sub-account discovery we hit `/accounts/{owner}/positions?forceFresh=true`,
 * which performs a live on-chain AccountLens read server-side (`source:
 * accountLensVaultInfo`) rather than serving indexed/cached data — so a
 * position opened seconds ago is visible.
 */

const EULER_DATA_API_BASE = 'https://v3.euler.finance/v3'

const EULER_API_REQUEST_TIMEOUT_MS = 10_000

/**
 * Chains the Euler Data API MAY serve. The authoritative roster is the API's
 * own `GET /v3/chains` index and it DRIFTS: re-probed 2026-08-22, it serves 11
 * chains — five listed here now answer a fast 404 (Sonic 146, TAC 239, Swell
 * 1923, BOB 60808, Berachain 80094) and take the on-chain 256 sub-account
 * scan in the build path, same as chains never listed (Optimism 10, Gnosis
 * 100, Worldchain 480, Mantle 5000, Ink 57073). The dead entries are KEPT
 * deliberately: a 404 costs one ~150 ms request, and keeping them means the
 * fallback self-heals the moment Euler re-indexes the chain — dropping an
 * entry would freeze that chain on the 256-call scan until someone edits this
 * file. Only a MISSING entry is a bug (Monad and Linea were: both served by
 * the API while every user read paid the full scan).
 */
export const EULER_API_CHAINS: ReadonlySet<string> = new Set([
  '1', // Ethereum
  '56', // BSC
  '130', // Unichain
  '143', // Monad — served; was missing while carrying 134 vaults
  '146', // Sonic — 404 as of 2026-08-22 (was served); kept, see above
  '239', // TAC — 404 as of 2026-08-22
  '999', // HyperEVM
  '1923', // Swell — 404 as of 2026-08-22
  '8453', // Base
  '9745', // Plasma
  '42161', // Arbitrum
  '43114', // Avalanche
  '59144', // Linea — served; was missing while carrying 88 vaults
  '60808', // BOB — 404 as of 2026-08-22
  '80094', // Berachain — 404 as of 2026-08-22
])

export const eulerApiSupportsChain = (chainId: string): boolean =>
  EULER_API_CHAINS.has(chainId)

interface EulerApiPosition {
  /** Sub-account address holding the position. */
  account: string
  subAccount?: { owner?: string }
}

/**
 * Fetches every position for `owner` across all of its sub-accounts on
 * `chainId`. With `forceFresh` the API does a live on-chain lens read.
 * Returns `undefined` when the chain is unsupported or the request fails —
 * callers distinguish this from "owner has no positions" (`[]`).
 */
export async function fetchEulerPositionsFromApi(
  chainId: string,
  owner: string,
  forceFresh = true,
): Promise<EulerApiPosition[] | undefined> {
  if (!eulerApiSupportsChain(chainId)) return undefined

  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    EULER_API_REQUEST_TIMEOUT_MS,
  )
  try {
    const positions: EulerApiPosition[] = []
    const limit = 100
    let offset = 0
    // Paginate so users with many positions still surface every sub-account.
    for (let page = 0; page < 10; page++) {
      const url =
        `${EULER_DATA_API_BASE}/accounts/${owner.toLowerCase()}/positions` +
        `?chainId=${chainId}&forceFresh=${forceFresh}&limit=${limit}&offset=${offset}`
      const response = await fetch(url, { signal: controller.signal })
      if (!response.ok) return offset === 0 ? undefined : positions

      const body = (await response.json()) as {
        data?: EulerApiPosition[]
        meta?: { total?: number }
      }
      const page_ = body?.data ?? []
      positions.push(...page_)

      const total = body?.meta?.total
      offset += limit
      if (
        page_.length < limit ||
        (typeof total === 'number' && offset >= total)
      )
        break
    }
    return positions
  } catch {
    return undefined
  } finally {
    clearTimeout(timeout)
  }
}
